import { useEffect, useMemo, useState } from 'react'

import { DatePicker } from '@/components/common/DatePicker'
import { toast } from '@/components/common/Toast'
import { Button, Label } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLocale, useTranslations } from '@/hooks/useTranslations'
import {
  useBulkUpdateTempWorkSessions,
  type TempWorkSession,
} from '@/modules/temp-worker/services/tempWorker'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sessions: TempWorkSession[]
  currentWorkDate: string
  onSuccess: (result: { newWorkDate: string; updatedCount: number }) => void
}

function ymdToDate(ymd: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
  if (!m) return undefined
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0)
}

function dateToYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function formatYmd(ymd: string, locale: string) {
  const d = ymdToDate(ymd)
  if (!d) return ymd
  return d.toLocaleDateString(locale === 'th' ? 'th-TH' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function BulkEditWorkDateDialog({
  open,
  onOpenChange,
  sessions,
  currentWorkDate,
  onSuccess,
}: Props) {
  const t = useTranslations('temp_workers')
  const tButtons = useTranslations('buttons')
  const locale = useLocale()
  const $bulk = useBulkUpdateTempWorkSessions()

  const [newWorkDate, setNewWorkDate] = useState(currentWorkDate)
  const [failedRows, setFailedRows] = useState<
    { session_id: string; worker_name: string; message: string }[]
  >([])

  useEffect(() => {
    if (open) {
      setNewWorkDate(currentWorkDate)
      setFailedRows([])
    }
  }, [open, currentWorkDate])

  const sessionNames = useMemo(
    () => sessions.map((s) => s.worker_name_snapshot),
    [sessions],
  )

  const previewNames = useMemo(() => {
    const max = 5
    if (sessionNames.length <= max) return sessionNames.join(', ')
    return `${sessionNames.slice(0, max).join(', ')} +${sessionNames.length - max}`
  }, [sessionNames])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (sessions.length === 0) return

    setFailedRows([])
    try {
      const result = await $bulk.mutateAsync({
        session_ids: sessions.map((s) => s.id),
        work_date: newWorkDate,
      })

      const { updated, failed } = result.data
      if (failed.length === 0) {
        toast.toastSuccess(t('bulk_edit_ok', { count: updated.length }))
        onOpenChange(false)
        onSuccess({ newWorkDate, updatedCount: updated.length })
        return
      }

      if (updated.length === 0) {
        toast.toastFailed(t('bulk_edit_all_failed'))
      } else {
        toast.toastFailed(
          t('bulk_edit_partial', { updated: updated.length, failed: failed.length }),
        )
        onSuccess({ newWorkDate, updatedCount: updated.length })
      }
      setFailedRows(failed)
    } catch (err: any) {
      toast.toastFailed(
        err?.response?.data?.message || err?.message || t('update_session_fail'),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bulk_edit_title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('bulk_edit_summary', { count: sessions.length })}
          </p>
          <p className="text-sm font-medium">{previewNames}</p>
          <p className="text-sm text-muted-foreground">
            {t('bulk_edit_from_to', {
              from: formatYmd(currentWorkDate, locale),
              to: formatYmd(newWorkDate, locale),
            })}
          </p>
          <div>
            <Label className="text-sm">{t('edit_session_work_date')}</Label>
            <DatePicker
              className="mt-1.5 h-10 w-full"
              value={ymdToDate(newWorkDate) ?? undefined}
              onChange={(d) => {
                if (!d) return
                setNewWorkDate(dateToYmd(d))
              }}
            />
          </div>

          {failedRows.length > 0 ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">{t('bulk_edit_failed_list')}</p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                {failedRows.map((row) => (
                  <li key={row.session_id}>
                    <span className="font-medium text-foreground">{row.worker_name}</span>
                    {' — '}
                    {row.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={$bulk.isLoading}
            >
              {tButtons('cancel')}
            </Button>
            <Button type="submit" disabled={$bulk.isLoading || sessions.length === 0}>
              {t('bulk_edit_confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
